import { getPaginatedCouponTemplates } from '../../../api/couponApi'
import CouponTemplateFilter from '../../../components/coupon-templates/coupon-template-filter/coupon-template-filter'
import LinkButton from '../../../components/link-button/link-button'
import {
  formatCouponTemplate,
  getDiscountDetail,
  getUsageRate,
} from '../../../helpers/coupon-template-helper'
import { keysToCamel } from '../../../helpers/keys-to-case'
import { getDateTime, getSearchParams, getSearchValues } from '../../../helpers/utils'
import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import { PageInfo, PermissionsEnum, TestSideEnums } from '../../../share-types'
import { ReduxStore } from '../../../store'
import { Plan, PlanTranslation } from '../../../store/global/reducer'
import { CouponTemplateStatusOptions, couponTypeList, TestSideOptions } from '../constant'
import {
  CouponTemplateCategoryEnums,
  couponTemplateCategoryOptions,
  CouponTemplateStatus,
  CouponTemplateTranslation,
  FormatCouponTemplate,
  ICouponTemplateFilterParams,
  IPaginatedCouponTemplateFilterParams,
  PaginatedCouponTemplate,
} from '../share-type'
import styles from './coupon-templates.module.less'
import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Col, Row, Space, Table, Tag, Tooltip, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { compose } from 'redux'

type PropsFromRedux = ConnectedProps<typeof withConnect>

type IProps = PropsFromRedux

const defaultPage = {
  pageSize: 10,
  currentPage: 1,
  count: 0,
}

const CouponTemplates = (props: IProps) => {
  const navigate = useNavigate()
  const { locale } = props
  const [couponTemplates, setCouponTemplates] = useState<FormatCouponTemplate[]>()
  const [pageInfo, setPageInfo] = useState<PageInfo>(defaultPage)
  const [searchValues, setSearchValues] = useState<ICouponTemplateFilterParams>()
  const [searchParams, setSearchParams] = useSearchParams()

  useEffect(() => {
    const newSearchValues = getSearchValues(
      [
        { key: 'name', isBoolean: false },
        { key: 'code', isBoolean: false },
        { key: 'status', isBoolean: false },
        { key: 'discountType', isBoolean: false },
        { key: 'startDate', isBoolean: false },
        { key: 'endDate', isBoolean: false },
        { key: 'size', isBoolean: false, default: defaultPage.pageSize },
        { key: 'page', isBoolean: false, default: defaultPage.currentPage - 1 },
        { key: 'testSide', isBoolean: false },
        { key: 'category', isBoolean: false },
      ],
      searchParams,
    )

    const data = {
      ...newSearchValues,
      size: Number(newSearchValues.size),
      page: Number(newSearchValues.page),
    }
    setSearchValues(newSearchValues)
    getPaginatedCouponTemplatesAction(data)
  }, [])

  const getPaginatedCouponTemplatesAction = (data: IPaginatedCouponTemplateFilterParams) => {
    getPaginatedCouponTemplates(data).then((res: PaginatedCouponTemplate) => {
      const formatResponse = res.data.map((item: any) => formatCouponTemplate(keysToCamel(item)))
      setCouponTemplates(formatResponse)
      setPageInfo({
        ...res.pageInfo,
        currentPage: res.pageInfo.currentPage + 1,
      })
    })
  }

  const onSearch = (values: ICouponTemplateFilterParams) => {
    const data = {
      page: 0,
      size: pageInfo.pageSize,
      ...values,
    }
    setSearchValues(values)
    setSearchParams(getSearchParams(values))
    getPaginatedCouponTemplatesAction(data)
  }

  const paginationOnChange = (page: number, pageSize: number) => {
    const data = {
      ...searchValues,
      size: pageSize,
      page: page - 1,
    }

    getPaginatedCouponTemplatesAction(data)
  }

  const columns: ColumnsType<FormatCouponTemplate> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: (a, b) => a.id - b.id,
      width: 50,
    },
    {
      title: translate('couponTemplates.name'),
      dataIndex: 'translations',
      key: 'couponName',
      render: (translations: CouponTemplateTranslation[], record: MISSING_TYPE) => {
        const couponNameList = translations.map(translation => translation.name)
        const couponName = couponNameList.join('/')
        return (
          <Space orientation="vertical">
            <Tooltip placement="topLeft" title={couponName}>
              <Typography.Text style={{ maxWidth: 200 }} ellipsis>
                {couponName}
              </Typography.Text>
            </Tooltip>
            <Typography.Link style={{ maxWidth: 200 }} ellipsis copyable={{ tooltips: false }}>
              {record.code}
            </Typography.Link>
          </Space>
        )
      },
    },
    {
      title: translate('couponTemplates.discountType'),
      dataIndex: 'discount',
      key: 'couponType',
      filters: couponTypeList,
      render: (discount: any) => {
        const couponType = couponTypeList.find(item => item.value === discount.discountType)
        return <span>{couponType?.label}</span>
      },
      filterIcon: <DownOutlined />,
      onFilter: (value, record) => record?.discount.discountType.indexOf(value) === 0,
    },
    {
      title: '优惠详情',
      dataIndex: 'discount',
      key: 'discountOff',
      render: (discount: any, record) => {
        return <span>{getDiscountDetail(discount, record.translations[0].discountOff)}</span>
      },
    },
    {
      title: '课包详情',
      dataIndex: 'plans',
      key: 'plans',
      render: (couponTemplatePlans: any[]) => {
        const displayPlans = couponTemplatePlans
          .map((item: { planId: number; bonusCredits: number | null; price: number | null }) => {
            const filterPlan = props.plans.find((plan: Plan) => plan.id === item.planId)
            if (filterPlan) {
              const name = filterPlan.translations.find(
                (t: PlanTranslation) => t.locale === locale,
              )?.name
              if (item.price && filterPlan.price !== item.price) {
                return `${filterPlan.credits}点-(¥${item.price / 100})-${filterPlan.id}-${name}`
              }
              if (item.bonusCredits && filterPlan.credits !== item.bonusCredits) {
                return `${filterPlan.credits}点-(+${item.bonusCredits}点)-${filterPlan.id}-${name}`
              }

              return `${filterPlan.id}-${name}-${filterPlan.credits}点`
            }
            return ''
          })
          .filter((item: string) => item)
        if (displayPlans.length) {
          const context = displayPlans.map((item, index) => {
            return (
              <Tooltip key={index} placement="topLeft" title={item}>
                <Typography.Text style={{ maxWidth: 250 }} ellipsis>
                  {item}
                </Typography.Text>
              </Tooltip>
            )
          })
          return <Space.Compact orientation="vertical">{context}</Space.Compact>
          // return <Popover content={context}><Button type="link" size="small">{translate('view')}</Button></Popover>
        } else {
          return ''
        }
      },
    },
    {
      title: translate('couponTemplates.availableTimeRange'),
      key: 'availableTimeRange',
      render: (record: FormatCouponTemplate) => {
        return (
          <Space.Compact orientation="vertical">
            <Tooltip key="startDate" placement="topLeft" title={getDateTime(record.startDate)}>
              <Typography.Text style={{ maxWidth: 100 }} ellipsis>
                {getDateTime(record.startDate)}
              </Typography.Text>
            </Tooltip>
            <Row>-</Row>
            <Tooltip key="endDate" placement="topLeft" title={getDateTime(record.endDate)}>
              <Typography.Text style={{ maxWidth: 100 }} ellipsis>
                {getDateTime(record.endDate)}
              </Typography.Text>
            </Tooltip>
          </Space.Compact>
        )
      },
      sorter: (a, b) => {
        if (dayjs(a.startDate).isBefore(b.startDate)) {
          return 1
        }
        return 0
      },
    },
    {
      title: translate('couponTemplates.usageRate'),
      key: 'usageRate',
      render: (record: FormatCouponTemplate) => {
        return (
          <Col>
            <Row>{getUsageRate(record.issuedCount, record.usedCount)}%</Row>
            <Row>
              ({record.usedCount}/{record.issuedCount})
            </Row>
          </Col>
        )
      },
      sorter: (a, b) =>
        Number(getUsageRate(a.issuedCount, a.usedCount)) -
        Number(getUsageRate(b.issuedCount, b.usedCount)),
    },
    {
      title: translate('couponTemplates.usageRate30Day'),
      key: 'usageRate30Day',
      render: (record: FormatCouponTemplate) => {
        return (
          <Col>
            <Row>{getUsageRate(record.issuedCount30Day, record.usedCount30Day)}%</Row>
            <Row>
              ({record.usedCount30Day}/{record.issuedCount30Day})
            </Row>
          </Col>
        )
      },
      sorter: (a, b) =>
        Number(getUsageRate(a.issuedCount30Day, a.usedCount30Day)) -
        Number(getUsageRate(b.issuedCount30Day, b.usedCount30Day)),
    },
    {
      title: translate('couponTemplates.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: CouponTemplateStatus) => {
        const tag = CouponTemplateStatusOptions.find(item => item.value === status)?.label
        return <Tag color={status === CouponTemplateStatus.INACTIVE ? 'red' : 'green'}>{tag}</Tag>
      },
      filters: CouponTemplateStatusOptions,
      filterIcon: <DownOutlined />,
      onFilter: (value, record) => record.status === value,
    },
    {
      title: translate('testSide'),
      dataIndex: 'testSide',
      key: 'testSide',
      render: (testSide: TestSideEnums) => {
        if (testSide) {
          const tag = TestSideOptions.find(item => item.value === testSide)?.label
          return <Tag color={testSide == TestSideEnums.CONTROL ? 'blue' : 'orange'}>{tag}</Tag>
        }
        return ''
      },
    },
    {
      title: translate('couponTemplates.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category?: CouponTemplateCategoryEnums) => {
        if (category) {
          return (
            <Tag>{couponTemplateCategoryOptions.find(item => item.value === category)?.label}</Tag>
          )
        }
        return ''
      },
    },
    {
      title: translate('updatedBy'),
      key: 'updatedBy',
      render: (record: { updatedAt: string; updatedByEmail?: string }) => {
        return (
          <Space.Compact orientation="vertical">
            <Tooltip key="updatedAt" placement="topLeft" title={record.updatedAt}>
              <Typography.Text style={{ maxWidth: 100 }} ellipsis>
                {record.updatedAt}
              </Typography.Text>
            </Tooltip>
            <Tooltip key="updatedBy" placement="topLeft" title={record.updatedByEmail}>
              <Typography.Text style={{ maxWidth: 100 }} ellipsis>
                {record.updatedByEmail}
              </Typography.Text>
            </Tooltip>
          </Space.Compact>
        )
      },
    },
    {
      title: translate('tableActionColumn'),
      key: 'action',
      width: 60,
      render: (text: string, record: any) => (
        <Space orientation="vertical">
          <AuthPermission permission={PermissionsEnum.COUPON_TEMPLATES_WRITE}>
            <LinkButton
              to={`/coupon-templates/${record.id}/edit`}
              disabled={record.status === CouponTemplateStatus.INACTIVE}
            >
              {translate('edit')}
            </LinkButton>
          </AuthPermission>

          <LinkButton to={`/coupon-templates/${record.id}`}>{translate('view')}</LinkButton>

          <AuthPermission permission={PermissionsEnum.COUPON_TEMPLATES_WRITE}>
            <LinkButton to={`/coupon-templates/${record.id}/duplicate`}>
              {translate('duplicate')}
            </LinkButton>
          </AuthPermission>
        </Space>
      ),
    },
  ]

  return (
    <Space className={styles['coupon-list-space']} orientation="vertical" size={16}>
      <Row justify="center" align="middle">
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['left-col']}>
          <div>优惠券模板列表</div>
        </Col>
        <Col xs={{ span: 24 }} sm={{ span: 12 }} className={styles['right-col']}>
          <AuthPermission permission={PermissionsEnum.COUPON_TEMPLATES_WRITE}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/coupon-templates/new')}
            >
              创建新的优惠券模板
            </Button>
          </AuthPermission>
        </Col>
      </Row>

      <div className={styles['filter-bar']}>
        <CouponTemplateFilter locale={locale} onSearch={onSearch} formData={searchValues} />
      </div>

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={couponTemplates}
        pagination={{
          current: pageInfo.currentPage,
          pageSize: pageInfo.pageSize,
          total: pageInfo.count,
          onChange: paginationOnChange,
        }}
      />
    </Space>
  )
}

const mapStateToProps = (state: ReduxStore) => {
  return {
    activeCities: state.cities.activeCities,
    locale: state.global.locale,
    plans: state.global.plans,
  }
}

const withConnect = connect(mapStateToProps)

export default compose(withConnect)(CouponTemplates)
